import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section, ArticleCard } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import DeepContentBlocks from "../components/DeepContentBlocks";
import { STATIC_HUB_DEEP_CONTENT } from "../config/staticHubDeepContent";
import { ArrowRight, Sparkles, Users, MessageSquare, HeartHandshake, Heart, ShieldCheck } from "lucide-react";
import AppCta from "../components/AppCta";

export default function RelationshipsPage() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState({ pillars: [], luvconnect: null });
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    API.get("/relationships/overview").then(({ data }) => setOverview(data));
    API.get("/content/articles?category=relationships&limit=12").then(({ data }) => setArticles(data.items || []));
  }, []);

  const icons = [Sparkles, MessageSquare, HeartHandshake, Heart, ShieldCheck, Users];
  const deep = STATIC_HUB_DEEP_CONTENT.relationships;

  return (
    <div data-testid="page-relationships">
      <section className="relative overflow-hidden hero-bg">
        <div className="app-container pt-16 pb-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow" data-testid="rel-eyebrow">{t("relationships.eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05] max-w-[16ch]" data-testid="rel-title">{t("relationships.title")}</h1>
            <p className="text-white/75 mt-5 max-w-[52ch] leading-relaxed body-copy">{t("relationships.subtitle")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/ask-donny" className="btn-primary text-sm" data-testid="rel-cta-donny">{t("relationships.cta_donny")}</Link>
              <Link to="/hub/category/relationships" className="btn-secondary text-sm" data-testid="rel-cta-hub">{t("relationships.cta_hub")}</Link>
            </div>
          </div>
          <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 max-w-md md:ml-auto">
            <img src="https://images.unsplash.com/photo-1515015520803-1a2e0aadb290?auto=format&fit=crop&w=900&q=85" alt={t("relationships.image_alt")} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <Section eyebrow={t("relationships.pillars_eyebrow")} title={t("relationships.pillars_title")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rel-pillars">
          {overview.pillars.map((p, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={p.slug} className="card-surface p-5">
                <Icon className="w-5 h-5 gold-text" />
                <div className="font-display text-xl mt-3">{p.title}</div>
                <p className="text-sm text-white/72 mt-2 leading-relaxed body-copy">{p.description}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {overview.luvconnect && (
        <Section eyebrow={t("relationships.featured_eyebrow")} title={overview.luvconnect.name} subtitle={overview.luvconnect.tagline}>
          <div className="card-surface p-6 md:p-10 grid md:grid-cols-3 gap-6 items-center" data-testid="rel-luvconnect">
            <div className="md:col-span-2">
              <p className="text-white/85 leading-relaxed body-copy">{overview.luvconnect.description}</p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-white/85">
                {(overview.luvconnect.highlights || []).map((h, i) => (
                  <li key={i} className="flex gap-2"><span className="gold-text">◆</span>{h}</li>
                ))}
              </ul>
              <a href={overview.luvconnect.cta_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="rel-luv-cta">{t("relationships.luv_cta")} <ArrowRight className="w-4 h-4" /></a>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <img src={overview.luvconnect.image} alt="Luvconnect" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-transparent to-transparent" />
            </div>
          </div>
        </Section>
      )}

      <Section eyebrow={t("relationships.editorial_eyebrow")} title={t("relationships.editorial_title")} subtitle={t("relationships.editorial_subtitle")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rel-articles">
          {articles.map((a) => <ArticleCard key={a.id} article={a} />)}
        </div>
      </Section>

      <DeepContentBlocks
        pageTitle={deep.pageTitle}
        editorial={deep.editorial}
        quotes={deep.quotes}
        products={deep.products}
        askDonnyPrompt={deep.askDonnyPrompt}
        testidPrefix="rel-deep"
      />

      {/* PRACTITIONERS — Couples & Relationship Coaches from the Marketplace */}
      <PractitionerRecommendations
        eyebrow={t("relationships.practitioners_eyebrow")}
        title={t("relationships.practitioners_title")}
        subtitle={t("relationships.practitioners_subtitle")}
        topic="relationships"
        limit={3}
        testid="rel-practitioner-recs"
      />

      <AppCta variant="relationships" testid="relationships-app-cta" />
    </div>
  );
}
